import { Group, Stack, useMantineTheme } from '@mantine/core';
import type { TeamOut } from '@repo/api-codegen';
import type { IconNames } from '@repo/foundations';
import { Icon, Switch, Text } from '@repo/foundations';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useSuspenseFeatureAccess } from '../../api';
import { useTeamManagement } from '../../api/hooks/team/teamsManagement';
import type { SidebarSettings } from '../../api/types/models/team';
import { getSideBarSettingValue } from '../../api/types/models/team';

const SIDEBAR_OPTIONS = [
	{
		key: 'home',
		iconName: 'home',
		label: 'Home',
	},
	{
		key: 'catalog',
		iconName: 'listDetails',
		label: 'Catalog',
	},
	{
		key: 'collections',
		iconName: 'folders',
		label: 'Collections',
	},
	{
		key: 'glossary',
		iconName: 'book',
		label: 'Glossary',
	},
	{
		key: 'documents',
		iconName: 'fileDescription',
		label: 'Documents',
	},
	{
		key: 'questions',
		iconName: 'messages',
		label: 'Questions',
	},
	{
		key: 'bi_dashboards',
		iconName: 'customDashboard',
		label: 'Dashboards',
	},
] as {
	key: keyof SidebarSettings;
	iconName: IconNames;
	label: string;
}[];

export function TeamSidebarTab({ team }: { team: TeamOut }) {
	const theme = useMantineTheme();
	const { updateTeam } = useTeamManagement(team.id);
	const { collectionsAccess, secodaCoreAccess } = useSuspenseFeatureAccess();

	const [settings, setSettings] = useState<SidebarSettings>({
		home: getSideBarSettingValue(team.sidebar_settings, 'home'),
		catalog: getSideBarSettingValue(team.sidebar_settings, 'catalog'),
		collections: getSideBarSettingValue(team.sidebar_settings, 'collections'),
		dictionary: getSideBarSettingValue(team.sidebar_settings, 'dictionary'),
		documents: getSideBarSettingValue(team.sidebar_settings, 'documents'),
		questions: getSideBarSettingValue(team.sidebar_settings, 'questions'),
		metrics: getSideBarSettingValue(team.sidebar_settings, 'metrics'),
		glossary: getSideBarSettingValue(team.sidebar_settings, 'glossary'),
		bi_dashboards: getSideBarSettingValue(
			team.sidebar_settings,
			'bi_dashboards'
		),
	});

	// Filter options to only show items that are currently visible in the sidebar
	const visibleSidebarOptions = useMemo(
		() =>
			SIDEBAR_OPTIONS.filter((item) => {
				if (item.key === 'collections') {
					return collectionsAccess;
				}

				if (item.key === 'home') {
					return !!secodaCoreAccess;
				}

				return true;
			}),
		[collectionsAccess, secodaCoreAccess]
	);

	const onChange = async (
		key: keyof SidebarSettings,
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newSettings = {
			...settings,
			[key]: e.currentTarget.checked,
		};

		setSettings(newSettings);
		await updateTeam({
			pathParams: {
				teamId: team.id,
			},
			body: {
				sidebar_settings: newSettings,
			},
		});
	};

	return (
		<Stack spacing="xs">
			<Text size="sm" fw={500}>
				Show these items in the sidebar
			</Text>
			<Stack
				sx={{
					borderRadius: 8,
					border: `1px solid ${theme.other.getColor('border/primary/default')}`,
				}}
				p={4}
				spacing={0}
			>
				{visibleSidebarOptions.map((item) => (
					<Group key={item.key} position="apart" p={6}>
						<Group spacing={8}>
							<Icon name={item.iconName} />
							<Text size="sm">{item.label}</Text>
						</Group>
						<Switch
							size="md"
							checked={settings[item.key]}
							onChange={(e) => onChange(item.key, e)}
							aria-label={`Show ${item.label} on the sidebar`}
						/>
					</Group>
				))}
			</Stack>
		</Stack>
	);
}
